import "./styles.less";

import React, { FC } from "react";
import { Skeleton, Space, Typography } from "antd";

import { BathroomsTag } from "./BathroomsTag/web";
import { BedroomsTag } from "./BedroomsTag/web";
import { GuestsTag } from "./GuestsTag/web";
import { M2Tag } from "./M2Tag/web";
import { RoomsTag } from "./RoomsTag/web";
import { useFilters } from "../../Filters/Filters.hook";
import { useTheme } from "../../../Styles/ThemeHook";
import { useTypologyTag } from "./TypologyTag.hook";

export interface TypologyTagProps {
	propertyType: string;
	operationType: string;
	bedrooms?: number | [number];
	bathrooms?: number | [number];
	rooms?: number | [number];
	m2?: number;
	hectares?: number;
	guests?: number;
	loading: boolean;
	showIcons?: boolean;
	className?: string;
	isEspecial?: Boolean;
}

export const TypologyTag: FC<TypologyTagProps> = ({
	propertyType,
	operationType,
	bedrooms,
	rooms,
	bathrooms,
	m2,
	hectares,
	guests,
	loading,
	showIcons = false,
	className = "",
	isEspecial,
}) => {
	const { showBathrooms, showBedrooms, showM2, showRooms, showGuests } = useTypologyTag({
		propertyType,
		operationType,
	});
	const { theme } = useTheme();
	const { filters } = useFilters();

	if (loading) return <TypologyTagSkeleton className={className} />;

	const separador = !showIcons ? <Typography.Text>·</Typography.Text> : null;

	const getMinBedroom = bedrooms => {
		if (Array.isArray(bedrooms)) {
			if (filters.bedrooms && filters.bedrooms[0]) {
				for (const rooms of bedrooms) {
					if (rooms >= filters.bedrooms[0]) {
						return rooms;
					}
				}
			} else {
				return bedrooms[0];
			}
		}

		return bedrooms;
	};

	const getMinRoom = rooms => {
		if (Array.isArray(rooms)) {
			if (filters.rooms && filters.rooms[0]) {
				for (const room of rooms) {
					if (room >= filters.rooms[0]) {
						return room;
					}
				}
			} else {
				return rooms[0];
			}
		}

		return rooms;
	};

	const getMinBathooms = bathrooms => {
		if (Array.isArray(bathrooms)) {
			if (filters.bathrooms && filters.bathrooms[0]) {
				for (const bathroom of bathrooms) {
					if (bathroom >= filters.bathrooms[0]) {
						return bathroom;
					}
				}
			} else {
				return bathrooms[0];
			}
		}

		return bathrooms;
	};

	return (
		<Space
			align={"baseline"}
			size={showIcons ? theme.spacing.lgSpacing : theme.spacing.xsSpacing}
			className={
				"property-typology-tag" + " " + className + (showIcons ? " show-icons" : "")
			}>
			{showBedrooms && (
				<BedroomsTag
					isEspecial={isEspecial}
					bedrooms={getMinBedroom(bedrooms)}
					icon={showIcons}
					prefix={Array.isArray(bedrooms) && bedrooms.length > 1}
				/>
			)}

			{showRooms && showBedrooms && separador}
			{showRooms && (
				<RoomsTag
					isEspecial={isEspecial}
					rooms={getMinRoom(rooms)}
					icon={showIcons}
					prefix={Array.isArray(rooms) && rooms.length > 1}
				/>
			)}

			{showBathrooms && (showBedrooms || showRooms) && separador}
			{showBathrooms && (
				<BathroomsTag
					isEspecial={isEspecial}
					bathrooms={getMinBathooms(bathrooms)}
					icon={showIcons}
					prefix={Array.isArray(bathrooms) && bathrooms.length > 1}
				/>
			)}

			{showM2 && m2 > 0 && (showBedrooms || showRooms || showBathrooms) && separador}
			{showM2 && m2 > 0 && (
				<M2Tag
					isEspecial={isEspecial}
					m2={m2}
					hectares={hectares}
					icon={showIcons}
					isMin={Array.isArray(rooms) && rooms.length > 1}
				/>
			)}

			{showGuests && (showBedrooms || showRooms || showBathrooms || showM2) && separador}
			{showGuests && <GuestsTag isEspecial={isEspecial} guests={guests} icon={showIcons} />}
		</Space>
	);
};

export const TypologyTagSkeleton = ({ className }) => {
	return (
		<Space align={"center"} className={"property-typology-tag" + " " + className}>
			<TagSkeleton />
			<TagSkeleton />
			<TagSkeleton />
		</Space>
	);
};

export const TagSkeleton = () => {
	return <Skeleton.Button size={"small"} active />;
};

